/**
 * Library categories (the "browse by topic" layer over the document store). A background routine on
 * the server rolls the graph's tags/topics up into a small set of human-friendly categories — each a
 * block with an icon and a document count — and this is the shared shape both the routine and the web
 * app speak. The categories are *derived* (recomputed regularly), never authored, so nothing here is
 * a source of truth: it is a cache of "what does this collection cover".
 */

/** The tag Readwise stamps on Kindle/Amazon book highlights. Its docs get their own category. */
export const READWISE_TAG = 'Readwise';

/** A browsable category: one block in the Library, backed by one or more rolled-up tags/topics. */
export interface Category {
  /** Stable slug id (from the display name), for React keys and selection. */
  id: string;
  /** Human-friendly display name, e.g. "Product & Engineering". */
  name: string;
  /** Google Material Symbols icon name (snake_case), e.g. "rocket_launch". */
  icon: string;
  /** The tag/topic names that roll up into this category. */
  tags: string[];
  /** Document ids that fall under this category, newest-first. */
  noteIds: string[];
  /** Number of documents in the category (== noteIds.length; carried for convenience). */
  count: number;
  /**
   * Marks a category the app treats specially. `readwise` is the book-highlights bucket: its docs are
   * shown only inside the category, never in the Library's general "recent documents" list.
   */
  special?: 'readwise';
}

/** The computed category set for one user — one Firestore doc, refreshed by the nightly routine. */
export interface UserCategories {
  userId: string;
  categories: Category[];
  /** When this set was last computed (epoch ms) — drives the lazy-recompute TTL. */
  computedAt: number;
  /**
   * A cheap fingerprint of the routine's input (the tag names + their doc counts). When it is
   * unchanged since the last run the routine skips the model call and just re-stamps `computedAt`.
   */
  signature: string;
}

/** Document ids that belong to the Readwise book-highlights category (excluded from the general list). */
export function readwiseNoteIds(categories: Category[]): string[] {
  return categories.find((c) => c.special === 'readwise')?.noteIds ?? [];
}
